import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, ShieldAlert, Plus, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner } from "@/components/tac";

const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none";

function daysUntil(exp) {
  if (!exp) return null;
  const d = new Date(String(exp).slice(0, 10));
  return Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
}

const BUCKETS = [
  { key: "expired", label: "Expired · dispatch locked", color: "rose", test: (d) => d !== null && d < 0 },
  { key: "expiring", label: "Expiring within 30 days", color: "amber", test: (d) => d !== null && d >= 0 && d <= 30 },
  { key: "soon", label: "Upcoming (31 - 90 days)", color: "cyan", test: (d) => d !== null && d > 30 && d <= 90 },
  { key: "future", label: "Active (90+ days / no date)", color: "emerald", test: (d) => d === null || d > 90 },
];

const DOT = { rose: "bg-rose-500", amber: "bg-amber-500", cyan: "bg-cyan-500", emerald: "bg-emerald-500" };
const TXT = { rose: "text-rose-400", amber: "text-amber-400", cyan: "text-cyan-400", emerald: "text-emerald-400" };

export default function Compliance() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["owner", "dispatcher"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "COI", holder: "", expires_at: "" });

  const { data, isLoading } = useQuery({ queryKey: ["compliance"], queryFn: async () => (await api.get("/compliance")).data });

  const add = async () => {
    try {
      await api.post("/compliance", { type: form.type, holder: form.holder, expires_at: form.expires_at ? `${form.expires_at}T00:00:00Z` : null });
      toast.success(`${form.type} added`); setOpen(false); setForm({ type: "COI", holder: "", expires_at: "" });
      qc.invalidateQueries({ queryKey: ["compliance"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const items = (data || []).map((c) => ({ ...c, _d: daysUntil(c.expires_at) }));

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>Never let a credential lapse</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Compliance calendar</h1>
          <p className="mt-2 text-sm text-slate-400">Upcoming COI, license & permit expirations by urgency.</p></div>
        {canEdit && <button data-testid="add-compliance-btn" onClick={() => setOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> Add record</button>}
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 space-y-8">
          {BUCKETS.map((b) => {
            const rows = items.filter((c) => b.test(c._d)).sort((a, z) => (a._d ?? 9e9) - (z._d ?? 9e9));
            if (!rows.length) return null;
            return (
              <div key={b.key} data-testid={`bucket-${b.key}`}>
                <div className="mb-3 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${DOT[b.color]}`} />
                  <h2 className={`font-mono-tac text-xs uppercase tracking-widest ${TXT[b.color]}`}>{b.label}</h2>
                  <span className="font-mono-tac text-[10px] text-slate-600">{rows.length}</span>
                </div>
                <div className="relative space-y-2 border-l border-slate-800 pl-5">
                  {rows.map((c) => (
                    <div key={c.id} data-testid={`compliance-row-${c.id}`} className="card-tac relative flex items-center justify-between p-4">
                      <span className={`absolute -left-[26px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full ${DOT[b.color]}`} />
                      <div className="flex items-center gap-3">
                        <ShieldAlert className={`h-4 w-4 ${TXT[b.color]}`} />
                        <div>
                          <div className="text-sm text-white">{c.holder} · <span className="font-mono-tac text-xs uppercase text-slate-400">{c.type}</span></div>
                          <div className="font-mono-tac text-[10px] text-slate-500">{c.expires_at ? `expires ${String(c.expires_at).slice(0, 10)}` : "no expiry on file"}</div>
                        </div>
                      </div>
                      <div className={`text-right font-mono-tac text-xs ${TXT[b.color]}`}>
                        {c._d === null ? "-" : c._d < 0 ? `${Math.abs(c._d)}d overdue` : `${c._d}d left`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {!items.length && <div className="card-tac flex flex-col items-center gap-3 py-16 text-center text-slate-500" data-testid="compliance-empty"><CalendarClock className="h-8 w-8 text-slate-700" /> No compliance records yet.</div>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="card-tac w-full max-w-md space-y-3 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold text-white">Add compliance record</h2><button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button></div>
            <select data-testid="compliance-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp}>
              {["COI", "License", "Permit"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input data-testid="compliance-holder-input" placeholder="Holder / description" value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })} className={inp} />
            <input data-testid="compliance-expiry-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={inp} />
            <button data-testid="compliance-submit" disabled={!form.holder} onClick={add} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Add record</button>
          </div>
        </div>
      )}
    </div>
  );
}
